import { DatasetStatus, PendingStatus } from '@components'
import { Phase } from '@submissions/submissions/config'

export interface Account {
  id: number
  name: string
  allow_approval: boolean
  created_by_id: number | null
  is_disabled: boolean
  primary_contact_id: number | null
  slug: string
  subdomain: string
  use_sso_login: boolean
  disabled_message?: string
  logo_slug?: string | null
  uuid?: string
}

export const Role = {
  OWNER: 'account_owner',
  MANAGER: 'manager',
  USER: 'standard_user',
} as const

export type Role = (typeof Role)[keyof typeof Role]

export type SubmissionErrorCode =
  | 4000
  | 4100
  | 4200
  | 4300
  | 4400
  | 4500
  | 4600
  | 5000
  | 5101
  | 5102
  | 5103
  | 5104
  | 5105
  | 5110
  | 5201
  | 5202
  | 5301
  | 5302
  | 5401
  | 5402
  | 5403

export interface SubmissionError {
  created_at: string
  data: string
  debug_data?: string
  error_phase: Phase
  id: number
  message: string
  submission_id: number
  type_code: SubmissionErrorCode
  updated_at: string
}

interface PermissionsBase {
  all_privileges: boolean
}
export type Permissions = PermissionsBase & Record<string, boolean>
export interface User {
  avatar_slug: string | null
  created_at: string
  created_by_id: number | null
  deleted_at: string | null
  disabled: boolean | null
  email: string
  failed_login_count: number
  id: number
  last_login: string | null
  location: string | null
  name: string
  password_updated_at: string | null
  phone: string | null
  remember_token: null
  role: Role | null
  sub: string | null
  super_admin: number | null
  title: string | null
  updated_at: string | null
  updated_by_id: number | null
  permissions: (keyof Permissions)[]
  mock?: boolean
  uuid: string
}

export interface ApprovalResult {
  id: number
  account_id: number
  user_id: number
  template_id: number
  dataset_submission_id: number
  approval_status: DatasetStatus | PendingStatus
  created_at: string | null
  updated_at: string | null
  user: User
}

interface DatasetRows {
  dataset_values: {
    cell_value: string | string[] | null
    cvid: string
    field_id: string
  }[]
}

export interface DatasetWithDatasetRows {
  dataset_rows: DatasetRows[]
  id: number
  identifier: string
  updated_at: string | null
  uuid: string
  creativeClassificationColumns: { field_id: string; [key: string]: any }[]
}

export interface ConnectorType {
  id: number
  name: string
  active: number
  realm_label: string
  target_label: string | null
  is_analytic: number
  created_at: string
  created_by_id: number
  updated_at: string
  updated_by_id: number
  slug: string
  auth_type: string
}

export interface Connector {
  account_id: number
  company: string
  connector_type: ConnectorType
  active: number
  auth_type: string
  created_at: string
  created_by_id: number
  id: number
  is_analytic: number
  name: string
  realm_label: string
  slug: string
  target_label: string | null
  updated_at: string
  updated_by_id: number
  connector_type_id: number
  description: string | null
  nickname: string | null
  username: string
}

export interface Target {
  id: number
  account_id: number
  name: string | null
  nickname: string | null
  ga_account_id: string | null
  ga_property_id: string | null
  ga_custom_data_source_id: string | null
  aws_bucket_id: string | null
  aws_bucket_folder_id: string | null
  ga_custom_data_source_schema: string | null
  box_folder_id: string | null
  ftp_path: string | null
  db_table_name: string | null
  remote_target_id: string | null
  query_parameter: null
  connector_id: number
  remote_realm_id: string | null
  realm_name: string | null
  deleted_at: string | null
  use_aa_confirmation_email: boolean
  aa_confirmation_email: string | null
}

interface MultiTarget {
  remote_realm_id: string | null
  remote_target_id: string | null
}

export enum PostStatus {
  success = 'success',
  complete = 'complete',
  pending = 'pending',
  error = 'error',
}

export interface Post {
  id: number
  pattern_template_field_id: number
  target_id: number
  multi_target_id: string | null
  remote_job_id: string | null
  posted_at: string | null
  status: PostStatus
  api_response: string | null
  error_message: string | null
  created_at: string | null
  updated_at: string | null
  dataset_submission_id: number
  revision_storage_path: string | null
  target: Target
  multi_target: MultiTarget | null
}

export type TargetWithConnector = Target & { connector: Connector }

export enum SubmissionTrigger {
  FILE_BASED_OVERWRITE = 'file_based_overwrite',
  MANUAL_RESUBMISSION = 'manual_resubmission',
}

export interface SubmissionName {
  id: number
  submission_id: number
  dataset_id: number
  submission_name: string
}

export interface Submission {
  approval_result: ApprovalResult | null
  completed_at: string | null
  created_at: string | null
  created_by: string | null
  dataset_id: number
  user_id?: number
  errors?: SubmissionError[]
  id: number
  indexing_at: string | null
  phase: Phase
  processing_at: string | null
  ready_to_process_at: string | null
  rows: number
  sending_to_outstreams_at: string | null
  started_at: string | null
  status: DatasetStatus | PendingStatus
  latest_submission_status?: DatasetStatus | PendingStatus
  source: string | null
  submission_id: number
  submission_name: string | null
  template_id: number
  template_name: string
  validating_at: string | null
  waiting_on_data_at: string | null
  waiting_on_outstreams_at: string | null
  submitted_by_user?: User
  trigger?: SubmissionTrigger
  dataset_posts?: Post[]
  number_of_rows?: number
  dataset?: DatasetWithDatasetRows
  is_pending_rollup?: boolean
}

export interface IPendingRollupDataset {
  can_delete: boolean
  created_by_id: number
  created_by_name: string
  has_pending_rows: boolean
  id: number
  is_pending_rollup: boolean
  last_updated: string
  name: string
  row_count: number
  status: string
  uuid: string
  submissions: Submission[]
}

export interface SubmissionSummary {
  datasetSubmissionId: string | number
  pendingCount: number
  completeCount: number
  errorCount: number
  lastChecked: string | null
}

export type GovernanceType =
  | 'cv_id'
  | 'date'
  | 'link-validation'
  | 'list'
  | 'link'
  | 'autofill'
  | 'shortened link'
  | 'qr-code'
  | 'deep-link'
  | 'autonumber'
  | 'pattern'
  | 'concatenation'
  | 'custom-bitlink'
  | 'property'
  | 'link-status'
  | 'creative'

export type SegmentType = null | 'constant' | 'variable' | 'autonumber' | 'date' | 'random'

export enum ColumnDataType {
  DEFAULT = 'default',
  USER = 'user',
  DATE = 'date',
  SUBMISSION = 'submission',
}

export interface TableColumn {
  label: string
  lookup: string
  type?: string
  width?: string
}

export const ListType = {
  dependent: 'dependent',
  drilldown: 'drill-down',
  dropdown: 'drop-down',
} as const
export type ListType = (typeof ListType)[keyof typeof ListType]

export type CalculatedFunctionName = 'split' | 'substring' | 'noop'

export type GovernableType = 'field' | 'template_field'

export interface Calculated {
  id: number
  transformation_id: number
  function: CalculatedFunctionName
  origin_field_id: number
  origin_field_type: GovernableType
  arguments: string[]
}

export interface Transformation {
  calculated?: Calculated[]
}

export interface IListColumn {
  created_at: string
  created_by_id: null | number
  id: number | string
  list_id: number
  list_values: {
    row_number: number
    value: string | null
    id: number | string | null
  }[]
  name: string
  nullable: null | boolean
  powered_template_field_id: null | number | string
  precedence: number
  require_unique: null | boolean
  updated_at: string
  updated_by_id: number
}

export const LIST_TYPES = {
  dependent: 'dependent',
  drillDown: 'drill-down',
  dropDown: 'drop-down',
} as const

export type TListTypes = (typeof LIST_TYPES)[keyof typeof LIST_TYPES]

export interface ListDTOResponse {
  account_id: number
  created_at: string
  created_by_id: number
  deleted_at: null | string
  id: number
  imported_from_target_id: null | number
  list_columns: IListColumn[]
  name: string
  notes: null | string
  orderedBy: string
  powered_template_id: null | number | string
  type: TListTypes
  updated_at: string
  updated_by_id: number
}

export interface AccountUserList {
  avatar_slug: string
  created_at: string
  created_by_id: null | number
  deleted_at: null | string
  disabled: null | boolean
  email: string
  failed_login_count: number
  id: number
  last_login: string
  location: null | string
  name: string
  password_updated_at: string
  phone: string
  remember_token: null | string
  role: string
  sub: string
  super_admin: number
  title: null | string
  updated_at: string
  updated_by_id: null | number
  uuid: string
}
